import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2,
  IconButton,
  Typography,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { Input } from "./Input";
import { visitorUpdate } from "../services/visitors";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { formatPhone, phoneValidate, unMask } from "../helpers/format";
import Loading from "./Loading";
import moment from "moment";
import { useEffect } from "react";

const VisitorEdit = ({
  open,
  onClose,
  refetch,
  visitor,
}: {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
  visitor: {
    id: string;
    name: string;
    phone?: string;
    visitDate: string | Date;
    observation?: string;
  } | null;
}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      visitorUpdate(visitor?.id || "", body),
    onSuccess: () => {
      toast.success("Visitante atualizado com sucesso");
      refetch();
      resetForm();
      setTimeout(() => {
        onClose();
      }, 300);
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const {
    values,
    handleSubmit,
    handleChange,
    setFieldValue,
    resetForm,
    setValues,
  } = useFormik({
    initialValues: {
      name: "",
      phone: "",
      visitDate: moment().format("YYYY-MM-DD"),
      observation: "",
    },
    onSubmit: (values) => {
      if (!values.name || values.name.trim() === "") {
        toast.error("Preencha o campo Nome");
        return;
      }

      if (!values.visitDate) {
        toast.error("Preencha o campo Data da Visita");
        return;
      }

      if (values.phone && !phoneValidate(formatPhone(values.phone))) {
        toast.error("Telefone inválido");
        return;
      }

      mutate({
        ...values,
        phone: values.phone ? unMask(values.phone) : null,
      });
    },
  });

  useEffect(() => {
    if (visitor && open) {
      setValues({
        name: visitor.name || "",
        phone: visitor.phone || "",
        visitDate: visitor.visitDate
          ? moment(visitor.visitDate).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
        observation: visitor.observation || "",
      });
    }
  }, [visitor, open, setValues]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { backgroundColor: "#1E1E1E", color: "#FFF" },
      }}
    >
      <DialogContent>
        <Grid2
          container
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5">Editar Visitante</Typography>
          <IconButton onClick={onClose}>
            <IconX />
          </IconButton>
        </Grid2>

        <form onSubmit={handleSubmit}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12 }}>
              <Input
                label="Nome *"
                placeholder="Digite o nome completo"
                name="name"
                value={values.name}
                onChange={handleChange}
                fullWidth
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Input
                label="Telefone"
                placeholder="Digite o telefone"
                name="phone"
                value={formatPhone(values.phone)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  setFieldValue("phone", unMask(value));
                }}
                fullWidth
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Input
                label="Data da Visita *"
                type="date"
                name="visitDate"
                value={values.visitDate}
                onChange={handleChange}
                fullWidth
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Input
                label="Observação"
                placeholder="Digite uma observação"
                name="observation"
                value={values.observation}
                onChange={handleChange}
                multiline
                rows={4}
                fullWidth
              />
            </Grid2>
          </Grid2>

          <DialogActions sx={{ mt: 3, px: 0 }}>
            <Button onClick={onClose} variant="outlined" color="secondary">
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isPending}
            >
              {isPending ? <Loading loading={true} /> : "Salvar"}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default VisitorEdit;
